/**
 * Menu de ações (⋯) da NFSe — mesmo comportamento da página Notas Fiscais.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as DescIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  MoreVert as MoreIcon,
  OpenInNew as OpenIcon,
  PictureAsPdf as PdfIcon,
  Share as ShareIcon,
  Sync as SyncIcon,
  Replay as ReplayIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import SendDocumentDialog from '../SendDocumentDialog';
import { mobileFullScreenDialogPaperSx } from '../../utils/mobileFormLayout';
import { NFSE_STATUS_LABELS, nfseCanReemitAfterCancel, nfseCanRetry, nfseHasPdf } from '../../constants/nfseStatusUi';
import useNfseServiceCodePrompt from '../../hooks/useNfseServiceCodePrompt';
import {
  clinicAccentChipSx,
  clinicColors,
  clinicDialogBannerSx,
  clinicDialogCancelButtonSx,
  clinicDialogContentSx,
  clinicDialogFooterSx,
  clinicDialogPaperSx,
  clinicDialogPrimaryButtonSx,
  clinicInfoCardLabelSx,
  clinicInputFieldSx,
  clinicMenuPaperSx,
  clinicMonoFont,
  clinicOutlinedButtonSx,
  clinicSectionTitleSx,
  clinicTealIconButtonSx,
} from '../../pages/Clinic/clinicUi';
import { Chip } from '@mui/material';
import { todayYmdBr } from '../../utils/localCalendarDate';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

export type ClinicNfseActionsProps = {
  nfse: Record<string, any>;
  onUpdated?: () => void;
  onNotify?: (message: string, severity?: 'success' | 'error' | 'warning') => void;
  patientId?: string;
  iconButtonSx?: object;
  stopPropagation?: boolean;
};

const ClinicNfseActions: React.FC<ClinicNfseActionsProps> = ({
  nfse,
  onUpdated,
  onNotify,
  patientId,
  iconButtonSx,
  stopPropagation = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { promptAndEmit, nfseServiceCodeDialog } = useNfseServiceCodePrompt();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeNfse, setActiveNfse] = useState<Record<string, any> | null>(null);

  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailNfse, setDetailNfse] = useState<any>(null);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelJustificativa, setCancelJustificativa] = useState('');
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDocData, setSendDocData] = useState<any>(null);

  const notify = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    onNotify?.(message, severity);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setActiveNfse(null);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (stopPropagation) event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setActiveNfse(nfse);
  };

  const menuItem = activeNfse || nfse;

  const openSendToPatient = (item: Record<string, any>, fromMenu = true) => {
    const p = item.patient;
    setSendDocData({
      id: item.id,
      patientId: item.patient_id || p?.id || patientId,
      email: item.tomador_email || p?.email,
      phone: item.tomador_telefone || p?.whatsapp || p?.phone,
      name: item.tomador_razao_social || item.tomador_nome || p?.full_name,
    });
    setSendDialogOpen(true);
    if (fromMenu) closeMenu();
  };

  const renderStatusChip = (status: string) => (
    <Chip
      label={NFSE_STATUS_LABELS[status] || status}
      size="small"
      sx={clinicAccentChipSx(
        { pending: clinicColors.warning, processando_autorizacao: '#0d9488', autorizado: clinicColors.positive, erro_autorizacao: clinicColors.negative, cancelado: clinicColors.slate }[status] || clinicColors.slate,
      )}
    />
  );

  const handleCheckStatus = async (id: string) => {
    try {
      const res = await axios.post(getApiUrl(`/clinic/nfse/${id}/check-status`));
      const st = res.data?.data?.status;
      notify(`Status atualizado: ${NFSE_STATUS_LABELS[st] || st}`);
      onUpdated?.();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Erro ao consultar status', 'error');
    }
  };

  const handleReemitAfterCancel = async (item: Record<string, any>) => {
    const id = item?.id;
    if (!id) return;
    closeMenu();
    try {
      setRetryingId(id);
      await promptAndEmit(
        {
          transactionId: item.transaction_id || undefined,
          receiptId: item.receipt_id || undefined,
        },
        async (choice) => {
          const res = await axios.post(getApiUrl(`/clinic/nfse/${id}/reemit`), choice);
          const st = res.data?.data?.status;
          if (st === 'erro_autorizacao') {
            const msg = res.data?.data?.error_message || 'A nota foi enviada, mas a prefeitura/SEFIN rejeitou.';
            notify(msg, 'error');
          } else {
            notify(`Nova NFSe enviada: ${NFSE_STATUS_LABELS[st] || st || 'processando'}`);
          }
          if (detailDialog && detailNfse?.id === id) {
            setDetailDialog(false);
          }
          onUpdated?.();
        },
      );
    } catch (err: any) {
      notify(err.response?.data?.error || err.message || 'Erro ao emitir nova NFSe', 'error');
    } finally {
      setRetryingId(null);
    }
  };

  const handleRetryEmit = async (id: string) => {
    try {
      setRetryingId(id);
      const res = await axios.post(getApiUrl(`/clinic/nfse/${id}/retry`), {
        issue_date: todayYmdBr(),
      });
      const st = res.data?.data?.status;
      if (st === 'erro_autorizacao') {
        const msg = res.data?.data?.error_message || 'A nota foi reenviada, mas a prefeitura/SEFIN rejeitou novamente.';
        notify(msg, 'error');
      } else {
        notify(`Reemissão enviada: ${NFSE_STATUS_LABELS[st] || st || 'processando'}`);
      }
      if (detailDialog && detailNfse?.id === id) {
        setDetailNfse(res.data?.data || detailNfse);
      }
      onUpdated?.();
      closeMenu();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Erro ao reemitir NFSe', 'error');
    } finally {
      setRetryingId(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelJustificativa || !menuItem?.id) return;
    try {
      await axios.post(getApiUrl(`/clinic/nfse/${menuItem.id}/cancel`), { justificativa: cancelJustificativa });
      notify('NFSe cancelada com sucesso');
      setCancelDialog(false);
      setCancelJustificativa('');
      closeMenu();
      onUpdated?.();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Erro ao cancelar', 'error');
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo || !menuItem?.id) return;
    try {
      const emails = emailTo.split(',').map((e) => e.trim()).filter(Boolean);
      await axios.post(getApiUrl(`/clinic/nfse/${menuItem.id}/email`), { emails });
      notify('Email enviado com sucesso');
      setEmailDialog(false);
      setEmailTo('');
      closeMenu();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Erro ao enviar email', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir este registro de NFSe?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/nfse/${id}`));
      notify('Registro excluído');
      closeMenu();
      onUpdated?.();
    } catch (err: any) {
      notify(err.response?.data?.error || 'Erro ao excluir', 'error');
    }
  };

  const fetchNfsePdfBlob = async (id: string, disposition: 'inline' | 'attachment') => {
    const res = await axios.get(getApiUrl(`/clinic/nfse/${id}/pdf`), {
      responseType: 'blob',
      params: { disposition },
    });
    return res.data as Blob;
  };

  const handleOpenNfsePdf = async (nf: any) => {
    if (!nf?.id) return;
    try {
      setPdfLoadingId(nf.id);
      const blob = await fetchNfsePdfBlob(nf.id, 'inline');
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
      closeMenu();
    } catch (err: any) {
      let message = 'Não foi possível abrir o PDF da NFSe';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.error || message;
        } catch { /* ignore */ }
      } else if (data?.error) {
        message = data.error;
      }
      notify(message, 'error');
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleDownloadNfsePdf = async (nf: any) => {
    if (!nf?.id) return;
    try {
      setPdfLoadingId(nf.id);
      const blob = await fetchNfsePdfBlob(nf.id, 'attachment');
      const number = String(nf.nfse_number || nf.id).replace(/[^\w.-]/g, '_');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NFSe_${number}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      closeMenu();
    } catch (err: any) {
      let message = 'Não foi possível baixar o PDF da NFSe';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.error || message;
        } catch { /* ignore */ }
      } else if (data?.error) {
        message = data.error;
      }
      notify(message, 'error');
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleViewDetail = async (id: string) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/nfse/${id}`));
      setDetailNfse(res.data?.data);
      setDetailDialog(true);
    } catch {
      notify('Erro ao carregar detalhes', 'error');
    }
    closeMenu();
  };

  if (!nfse?.id) return null;

  return (
    <>
      <Tooltip title="Ações da nota fiscal">
        <IconButton
          size="small"
          onClick={openMenu}
          sx={iconButtonSx || clinicTealIconButtonSx}
          aria-label="Ações da NFSe"
        >
          <MoreIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{ sx: clinicMenuPaperSx }}
      >
        <MenuItem onClick={() => menuItem && handleViewDetail(menuItem.id)}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Ver detalhes</ListItemText>
        </MenuItem>
        {menuItem?.status === 'processando_autorizacao' || menuItem?.status === 'pending' ? (
          <MenuItem onClick={() => { menuItem && handleCheckStatus(menuItem.id); closeMenu(); }}>
            <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Consultar status</ListItemText>
          </MenuItem>
        ) : null}
        {menuItem && nfseCanRetry(menuItem) && (
          <MenuItem
            onClick={() => handleRetryEmit(menuItem.id)}
            disabled={retryingId === menuItem.id}
          >
            <ListItemIcon>
              {retryingId === menuItem.id ? (
                <CircularProgress size={18} />
              ) : (
                <ReplayIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>Tentar reemitir</ListItemText>
          </MenuItem>
        )}
        {menuItem && nfseCanReemitAfterCancel(menuItem) && (
          <MenuItem
            onClick={() => handleReemitAfterCancel(menuItem)}
            disabled={retryingId === menuItem.id}
          >
            <ListItemIcon>
              {retryingId === menuItem.id ? (
                <CircularProgress size={18} />
              ) : (
                <AddIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>Emitir nova nota (substituta)</ListItemText>
          </MenuItem>
        )}
        {menuItem && nfseHasPdf(menuItem) && (
          <MenuItem onClick={() => handleOpenNfsePdf(menuItem)} disabled={pdfLoadingId === menuItem.id}>
            <ListItemIcon>
              {pdfLoadingId === menuItem.id ? <CircularProgress size={18} /> : <PdfIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>Abrir PDF da nota</ListItemText>
          </MenuItem>
        )}
        {menuItem && nfseHasPdf(menuItem) && (
          <MenuItem onClick={() => handleDownloadNfsePdf(menuItem)} disabled={pdfLoadingId === menuItem.id}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Baixar PDF</ListItemText>
          </MenuItem>
        )}
        {menuItem?.url_nfse && (
          <MenuItem onClick={() => { window.open(menuItem.url_nfse, '_blank'); closeMenu(); }}>
            <ListItemIcon><OpenIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Ver no portal nacional</ListItemText>
          </MenuItem>
        )}
        {menuItem?.status === 'autorizado' && (
          <MenuItem onClick={() => { setEmailTo(menuItem.tomador_email || ''); setEmailDialog(true); }}>
            <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Enviar por email</ListItemText>
          </MenuItem>
        )}
        {menuItem && nfseHasPdf(menuItem) && (
          <MenuItem onClick={() => openSendToPatient(menuItem)}>
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Enviar PDF (WhatsApp / e-mail)</ListItemText>
          </MenuItem>
        )}
        {menuItem?.status === 'autorizado' && (
          <MenuItem onClick={() => setCancelDialog(true)}>
            <ListItemIcon><CancelIcon fontSize="small" sx={{ color: clinicColors.negative }} /></ListItemIcon>
            <ListItemText sx={{ color: clinicColors.negative }}>Cancelar NFSe</ListItemText>
          </MenuItem>
        )}
        {(menuItem?.status === 'erro_autorizacao' || menuItem?.status === 'pending') && (
          <MenuItem onClick={() => { menuItem && handleDelete(menuItem.id); }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: clinicColors.negative }} /></ListItemIcon>
            <ListItemText sx={{ color: clinicColors.negative }}>Excluir registro</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{ sx: isMobile ? mobileFullScreenDialogPaperSx : clinicDialogPaperSx }}
      >
        <Box sx={clinicDialogBannerSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <DescIcon />
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Detalhes da NFSe</Typography>
          </Box>
          <IconButton onClick={() => setDetailDialog(false)} sx={{ color: '#fff' }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ ...clinicDialogContentSx, pt: 2 }}>
          {detailNfse && (
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography sx={clinicInfoCardLabelSx}>Nº NFSe</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: clinicColors.navy }}>{detailNfse.nfse_number || '—'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography sx={clinicInfoCardLabelSx}>Status</Typography>
                <Box sx={{ mt: 0.5 }}>{renderStatusChip(detailNfse.status)}</Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography sx={clinicInfoCardLabelSx}>Valor</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: clinicColors.navy }}>{formatCurrency(detailNfse.amount)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography sx={clinicInfoCardLabelSx}>Data de emissão</Typography>
                <Typography variant="body1" sx={{ color: clinicColors.slate }}>
                  {detailNfse.issue_date ? new Date(detailNfse.issue_date).toLocaleDateString('pt-BR') : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}><Typography sx={clinicSectionTitleSx}>Tomador</Typography></Grid>
              <Grid item xs={6} md={4}>
                <Typography sx={clinicInfoCardLabelSx}>Nome</Typography>
                <Typography variant="body2" sx={{ color: clinicColors.slate }}>{detailNfse.tomador_nome || '—'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography sx={clinicInfoCardLabelSx}>CPF/CNPJ</Typography>
                <Typography variant="body2" sx={{ color: clinicColors.slate }}>{detailNfse.tomador_cpf_cnpj || '—'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography sx={clinicInfoCardLabelSx}>E-mail</Typography>
                <Typography variant="body2" sx={{ color: clinicColors.slate }}>{detailNfse.tomador_email || '—'}</Typography>
              </Grid>
              <Grid item xs={12}><Typography sx={clinicSectionTitleSx}>Serviço</Typography></Grid>
              <Grid item xs={12}>
                <Typography sx={clinicInfoCardLabelSx}>Discriminação</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: clinicColors.slate }}>{detailNfse.description}</Typography>
              </Grid>
              {nfseHasPdf(detailNfse) && (
                <Grid item xs={12}>
                  <Typography sx={clinicInfoCardLabelSx}>Documento</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Button
                      size="small"
                      startIcon={pdfLoadingId === detailNfse.id ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                      onClick={() => handleOpenNfsePdf(detailNfse)}
                      disabled={pdfLoadingId === detailNfse.id}
                      sx={clinicOutlinedButtonSx}
                    >
                      Abrir PDF
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadNfsePdf(detailNfse)}
                      disabled={pdfLoadingId === detailNfse.id}
                      sx={clinicOutlinedButtonSx}
                    >
                      Baixar PDF
                    </Button>
                  </Stack>
                </Grid>
              )}
              {detailNfse.url_nfse && (
                <Grid item xs={12}>
                  <Typography sx={clinicInfoCardLabelSx}>Consulta pública</Typography>
                  <Button
                    size="small"
                    startIcon={<OpenIcon />}
                    onClick={() => window.open(detailNfse.url_nfse, '_blank')}
                    sx={{ ...clinicOutlinedButtonSx, mt: 0.5 }}
                  >
                    Abrir no portal nacional
                  </Button>
                </Grid>
              )}
              {detailNfse.error_message && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>{detailNfse.error_message}</Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={clinicDialogFooterSx}>
          {detailNfse?.status === 'processando_autorizacao' && (
            <Button
              startIcon={<SyncIcon />}
              onClick={() => { handleCheckStatus(detailNfse.id); setDetailDialog(false); }}
              sx={clinicOutlinedButtonSx}
            >
              Consultar status
            </Button>
          )}
          {detailNfse && nfseCanRetry(detailNfse) && (
            <Button
              startIcon={retryingId === detailNfse.id ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
              onClick={() => handleRetryEmit(detailNfse.id)}
              disabled={retryingId === detailNfse.id}
              variant="contained"
              sx={clinicDialogPrimaryButtonSx}
            >
              Tentar reemitir
            </Button>
          )}
          {detailNfse && nfseCanReemitAfterCancel(detailNfse) && (
            <Button
              startIcon={retryingId === detailNfse.id ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              onClick={() => handleReemitAfterCancel(detailNfse)}
              disabled={retryingId === detailNfse.id}
              variant="contained"
              sx={clinicDialogPrimaryButtonSx}
            >
              Emitir nova nota
            </Button>
          )}
          {detailNfse && nfseHasPdf(detailNfse) && (
            <Button
              startIcon={<ShareIcon />}
              onClick={() => openSendToPatient(detailNfse, false)}
              sx={clinicOutlinedButtonSx}
            >
              Enviar PDF ao paciente
            </Button>
          )}
          <Button onClick={() => setDetailDialog(false)} sx={clinicDialogCancelButtonSx}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: clinicDialogPaperSx }}>
        <Box sx={{ ...clinicDialogBannerSx, background: `linear-gradient(135deg, ${clinicColors.negative} 0%, #b91c1c 100%)` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon />
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Cancelar NFSe</Typography>
          </Box>
          <IconButton onClick={() => setCancelDialog(false)} sx={{ color: '#fff' }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={clinicDialogContentSx}>
          <Typography variant="body2" sx={{ mb: 2, color: clinicColors.slate }}>
            Informe a justificativa para o cancelamento. Esta ação não pode ser desfeita.
          </Typography>
          <TextField
            fullWidth
            label="Justificativa *"
            value={cancelJustificativa}
            onChange={(e) => setCancelJustificativa(e.target.value)}
            multiline
            rows={3}
            sx={clinicInputFieldSx}
          />
        </DialogContent>
        <DialogActions sx={clinicDialogFooterSx}>
          <Button onClick={() => { setCancelDialog(false); setCancelJustificativa(''); }} sx={clinicDialogCancelButtonSx}>Voltar</Button>
          <Button
            variant="contained"
            onClick={handleCancel}
            disabled={!cancelJustificativa}
            sx={{ ...clinicDialogPrimaryButtonSx, bgcolor: clinicColors.negative, '&:hover': { bgcolor: '#b91c1c' } }}
          >
            Confirmar cancelamento
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={emailDialog} onClose={() => setEmailDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: clinicDialogPaperSx }}>
        <Box sx={clinicDialogBannerSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon />
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Enviar NFSe por e-mail</Typography>
          </Box>
          <IconButton onClick={() => setEmailDialog(false)} sx={{ color: '#fff' }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={clinicDialogContentSx}>
          <TextField
            fullWidth
            label="E-mail(s)"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            helperText="Separe múltiplos e-mails com vírgula"
            sx={clinicInputFieldSx}
          />
        </DialogContent>
        <DialogActions sx={clinicDialogFooterSx}>
          <Button onClick={() => setEmailDialog(false)} sx={clinicDialogCancelButtonSx}>Cancelar</Button>
          <Button variant="contained" onClick={handleSendEmail} disabled={!emailTo} startIcon={<EmailIcon />} sx={clinicDialogPrimaryButtonSx}>
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <SendDocumentDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        documentType="nfse"
        documentId={sendDocData?.id || ''}
        patientId={sendDocData?.patientId || patientId}
        patientEmail={sendDocData?.email}
        patientPhone={sendDocData?.phone}
        patientWhatsapp={sendDocData?.phone}
        patientName={sendDocData?.name}
        documentLabel="Nota Fiscal (NFSe)"
      />

      {nfseServiceCodeDialog}
    </>
  );
};

export default ClinicNfseActions;
